import { Dialog, DialogContent, DialogTitle } from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { CornerDownLeftIcon, SearchIcon } from 'lucide-react';
import { type JSX, type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useCommonContent } from '../content/sections/common.js';
import { useNavContent } from '../content/sections/nav.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { filterNavByRole, NAV_ITEMS } from '../lib/nav.js';

interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/* Command palette do backoffice (ADR-0700, §7.5): busca + navegação rápida por teclado (⌘K/Ctrl+K
   abre — ver AdminLayout). Lista os destinos de navegação filtrados por role; setas movem a seleção,
   Enter navega. Sem dependência nova (Dialog + input + lista). */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const nav = useNavContent();
  const session = useStaffSession();
  const copy = useCommonContent().commandPalette;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const items = useMemo(
    () => (session === null ? [] : filterNavByRole(NAV_ITEMS, session.role)),
    [session],
  );
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return items;
    return items.filter(
      /* A busca casa o rótulo TRADUZIDO, não o identificador: quem opera em inglês digita
         "Companies" e precisa encontrar a tela. Casar pelo valor interno faria a busca
         funcionar só em português. */
      (i) =>
        (nav.items[i.to] ?? '').toLowerCase().includes(q) ||
        (nav.groups[i.group] ?? '').toLowerCase().includes(q),
    );
    /* `nav` nas deps não é formalidade: sem ele o resultado da busca fica preso ao idioma
       em que foi calculado, e trocar de idioma com o palette aberto devolveria os rótulos
       antigos. */
  }, [items, query, nav.items, nav.groups]);

  /* Reset ao abrir + clamp da seleção ao resultado. */
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, results.length - 1)));
  }, [results.length]);

  const go = (to: (typeof NAV_ITEMS)[number]['to']): void => {
    onOpenChange(false);
    navigate({ to }).catch(() => undefined);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[active];
      if (target !== undefined) go(target.to);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 overflow-hidden p-0" showCloseButton={false}>
        <DialogTitle className="sr-only">{copy.srTitle}</DialogTitle>
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <SearchIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          {/* O Dialog (Radix) foca o primeiro elemento focável ao abrir — este input. */}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={copy.placeholder}
            aria-label={copy.ariaLabel}
            className="h-12 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ul className="max-h-80 overflow-y-auto p-2">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              {copy.noResults}
            </li>
          ) : (
            results.map((item, i) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <button
                    type="button"
                    onClick={() => go(item.to)}
                    onMouseMove={() => setActive(i)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                      i === active
                        ? 'bg-accent/15 text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="flex-1 truncate text-foreground">{nav.items[item.to]}</span>
                    <span className="text-xs text-muted-foreground/70">{item.group}</span>
                    {i === active && (
                      <CornerDownLeftIcon
                        className="size-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
